import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type DateTimePickerResponseType,
    type DateTimePickerDateRange,
    NCoreUIKitLocalize,
    formatDateTime,
    DateTimePicker
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "DateTimePicker";

type ConfirmCancelEvent = {
    kind: "cancel";
};

type ConfirmOkEvent = {
    date: Date;
    kind: "ok";
};

type ConfirmEvent = ConfirmCancelEvent | ConfirmOkEvent;

const USAGE_CODE = `const [dueDate, setDueDate] = useState<Date>();

<DateTimePicker
    onChange={({ date }) => setDueDate(date instanceof Date ? date : undefined)}
    isClearEnabled={true}
    title="Due date"
    pickerType="date"
/>`;

const FORMAT_CODE = `<DateTimePicker
    onChange={({ date }) => saveDueDate(date)}
    titleFormat="DD MMMM YYYY dddd"
    returnFormat="YYYY-MM-DD"
    title="Due date"
    pickerType="date"
/>`;

const CONFIRM_CODE = `<DateTimePicker
    onOk={({ dateRange }) => setLeave(dateRange)}
    onCancel={() => setLeave(undefined)}
    isWorkWithRealtime={false}
    title="Leave dates"
    pickerType="date"
    variant="range"
/>`;

const REF_CODE = `const pickerRef = useRef<IDateTimePickerRef>(null);

<DateTimePicker
    pickerType="date"
    title="Due date"
    ref={pickerRef}
/>

pickerRef.current?.setDate(new Date());
pickerRef.current?.focus();
pickerRef.current?.clear();`;

const LAYOUT_CODE = `<DateTimePicker
    onChange={({ dateRange }) => setTrip(dateRange)}
    isShowWeekNumber={true}
    title="Trip dates"
    pickerType="date"
    variant="range"
    monthCount={2}
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const LABEL_CODE = `<DateTimePicker
    placeholder="Due date"
    accessibilityLabel="Due date"
    spreadBehaviour="stretch"
    pickerType="date"
/>`;

const SAMPLE_DATE = new Date(2026, 9, 14, 14, 30);

const SAMPLE_PAST_DATE = new Date(2026, 8, 1);

const TODAY = new Date();

const DAY_LIMIT = 14;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("dateTimePickerPage.pickerTypeTitle"),
        id: "picker-type"
    },
    {
        title: t("dateTimePickerPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("dateTimePickerPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("dateTimePickerPage.confirmTitle"),
        id: "confirm"
    },
    {
        title: t("dateTimePickerPage.labelTitle"),
        id: "label"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DateTimePickerPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const [
        dueDate,
        setDueDate
    ] = useState<Date | undefined>(undefined);

    const [
        leave,
        setLeave
    ] = useState<DateTimePickerDateRange>({});

    const [
        confirmEvent,
        setConfirmEvent
    ] = useState<ConfirmEvent | undefined>(undefined);

    const formatDate = (date: Date) => formatDateTime({
        locale: activeLocale,
        preset: "date",
        date
    });

    const handleDueDateChange = ({
        date
    }: DateTimePickerResponseType) => {
        setDueDate(date instanceof Date ? date : undefined);
    };

    const handleLeaveChange = ({
        dateRange
    }: DateTimePickerResponseType) => {
        setLeave({
            start: dateRange?.start instanceof Date ? dateRange.start : undefined,
            end: dateRange?.end instanceof Date ? dateRange.end : undefined
        });
    };

    const handleConfirmOk = ({
        date
    }: DateTimePickerResponseType) => {
        if(!(date instanceof Date)) {
            return;
        }

        setConfirmEvent({
            kind: "ok",
            date
        });
    };

    const handleConfirmCancel = () => {
        setConfirmEvent({
            kind: "cancel"
        });
    };

    const getConfirmCaption = () => {
        if(!confirmEvent) {
            return t("dateTimePickerPage.confirmEmpty");
        }

        if(confirmEvent.kind === "cancel") {
            return t("dateTimePickerPage.cancelled");
        }

        return t("dateTimePickerPage.confirmed", [
            formatDateTime({
                date: confirmEvent.date,
                locale: activeLocale,
                preset: "dateTime"
            })
        ]);
    };

    const getDueDateCaption = () => {
        if(!dueDate) {
            return t("dateTimePickerPage.overviewCaption");
        }

        return t("dateTimePickerPage.selectedCaption", [
            formatDate(dueDate)
        ]);
    };

    const getLeaveCaption = () => {
        if(!leave.start || !leave.end) {
            return t("dateTimePickerPage.rangeEmptyCaption", [
                DAY_LIMIT
            ]);
        }

        return t("dateTimePickerPage.rangeCaption", [
            formatDate(leave.start),
            formatDate(leave.end)
        ]);
    };

    return <Article>
        <ComponentHeader
            lead={t("dateTimePickerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("dateTimePickerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={getDueDateCaption()}
            >
                <DateTimePicker
                    title={t("dateTimePickerPage.dueDate")}
                    onChange={handleDueDateChange}
                    isClearEnabled={true}
                    pickerType="date"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("dateTimePickerPage.pickerTypeTitle")}
            id="picker-type"
        >
            <Paragraph>
                {t("dateTimePickerPage.pickerTypeText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <DateTimePicker
                    title={t("dateTimePickerPage.dueDate")}
                    initialDate={SAMPLE_DATE}
                    spreadBehaviour="stretch"
                    pickerType="date"
                />
                <DateTimePicker
                    title={t("dateTimePickerPage.reminder")}
                    spreadBehaviour="stretch"
                    initialDate={SAMPLE_DATE}
                    pickerType="time"
                />
                <DateTimePicker
                    title={t("dateTimePickerPage.meeting")}
                    spreadBehaviour="stretch"
                    initialDate={SAMPLE_DATE}
                    pickerType="date-time"
                />
                <DateTimePicker
                    title={t("dateTimePickerPage.meetingTwelve")}
                    isWorkWith24HoursFormat={false}
                    spreadBehaviour="stretch"
                    initialDate={SAMPLE_DATE}
                    pickerType="date-time"
                />
            </Preview>
            <CodeBlock
                code={FORMAT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateTimePickerPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("dateTimePickerPage.rangeText")}
            </Paragraph>
            <Preview
                caption={getLeaveCaption()}
                isStacked={true}
            >
                <DateTimePicker
                    title={t("dateTimePickerPage.leave")}
                    multipleSelectDayLimit={DAY_LIMIT}
                    onChange={handleLeaveChange}
                    spreadBehaviour="stretch"
                    isClearEnabled={true}
                    pickerType="date"
                    variant="range"
                    minDate={TODAY}
                />
            </Preview>
        </Section>
        <Section
            title={t("dateTimePickerPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("dateTimePickerPage.layoutText")}
            </Paragraph>
            <Preview
                caption={t("dateTimePickerPage.layoutCaption")}
                isStacked={true}
            >
                <DateTimePicker
                    title={t("dateTimePickerPage.tripDates")}
                    spreadBehaviour="stretch"
                    isShowWeekNumber={true}
                    isClearEnabled={true}
                    pickerType="date"
                    variant="range"
                    monthCount={2}
                />
            </Preview>
            <CodeBlock
                code={LAYOUT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateTimePickerPage.confirmTitle")}
            id="confirm"
        >
            <Paragraph>
                {t("dateTimePickerPage.confirmText")}
            </Paragraph>
            <Preview
                caption={getConfirmCaption()}
                isStacked={true}
            >
                <DateTimePicker
                    title={t("dateTimePickerPage.meeting")}
                    onCancel={handleConfirmCancel}
                    isWorkWithRealtime={false}
                    spreadBehaviour="stretch"
                    onOk={handleConfirmOk}
                    pickerType="date-time"
                />
            </Preview>
            <CodeBlock
                code={CONFIRM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dateTimePickerPage.labelTitle")}
            id="label"
        >
            <Paragraph>
                {t("dateTimePickerPage.labelText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <DateTimePicker
                    placeholder={t("dateTimePickerPage.labelPlaceholder")}
                    accessibilityLabel={t("dateTimePickerPage.dueDate")}
                    spreadBehaviour="stretch"
                    pickerType="date"
                />
            </Preview>
            <CodeBlock
                code={LABEL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("dateTimePickerPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <DateTimePicker
                        spreadBehaviour="stretch"
                        initialDate={SAMPLE_DATE}
                        isClearEnabled={true}
                        pickerType="date"
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("dateTimePickerPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <DateTimePicker
                    title={t("dateTimePickerPage.disabled")}
                    initialDate={SAMPLE_DATE}
                    spreadBehaviour="stretch"
                    isDisabled={true}
                    pickerType="date"
                />
                <DateTimePicker
                    hintText={t("dateTimePickerPage.readOnlyHint")}
                    title={t("dateTimePickerPage.createdAt")}
                    initialDate={SAMPLE_DATE}
                    spreadBehaviour="stretch"
                    isReadOnly={true}
                />
                <DateTimePicker
                    title={t("dateTimePickerPage.loading")}
                    initialDate={SAMPLE_DATE}
                    spreadBehaviour="stretch"
                    pickerType="date"
                    isLoading={true}
                />
                <DateTimePicker
                    hintText={t("dateTimePickerPage.dangerHint")}
                    title={t("dateTimePickerPage.endDate")}
                    initialDate={SAMPLE_PAST_DATE}
                    spreadBehaviour="stretch"
                    pickerType="date"
                    type="danger"
                />
                <DateTimePicker
                    hintText={t("dateTimePickerPage.requiredHint")}
                    title={t("dateTimePickerPage.birthDate")}
                    spreadBehaviour="stretch"
                    pickerType="date"
                    isRequired={true}
                />
                <DateTimePicker
                    title={t("dateTimePickerPage.reminder")}
                    spreadBehaviour="stretch"
                    isOptional={true}
                    pickerType="time"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("dateTimePickerPage.a11yTrigger"),
                    t("dateTimePickerPage.a11yField"),
                    t("dateTimePickerPage.a11yPanel"),
                    t("dateTimePickerPage.a11yCalendar"),
                    t("dateTimePickerPage.a11yClear"),
                    t("dateTimePickerPage.a11yReadOnly"),
                    t("dateTimePickerPage.a11yTime"),
                    t("dateTimePickerPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("dateTimePickerPage.propsFieldIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimePickerPage.propPickerType"),
                        type: "\"date\" | \"time\" | \"date-time\"",
                        defaultValue: "\"date-time\"",
                        name: "pickerType"
                    },
                    {
                        description: t("dateTimePickerPage.propVariant"),
                        type: "\"single\" | \"range\" | \"rrule\"",
                        defaultValue: "\"single\"",
                        name: "variant"
                    },
                    {
                        description: t("dateTimePickerPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("dateTimePickerPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("dateTimePickerPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("dateTimePickerPage.propIsShowSubTitle"),
                        name: "isShowSubTitle",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("dateTimePickerPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("dateTimePickerPage.propInitialDate"),
                        name: "initialDate",
                        type: "Date"
                    },
                    {
                        description: t("dateTimePickerPage.propInitialDateRange"),
                        type: "DateTimePickerDateRange",
                        name: "initialDateRange"
                    },
                    {
                        description: t("dateTimePickerPage.propInitialDateRule"),
                        name: "initialDateRule",
                        type: "RRule"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("dateTimePickerPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("dateTimePickerPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("dateTimePickerPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("dateTimePickerPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsClearEnabled"),
                        name: "isClearEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("dateTimePickerPage.propRightIcon"),
                        type: "NCoreUIKitIcon",
                        name: "rightIcon"
                    },
                    {
                        description: t("dateTimePickerPage.propHintTextIcon"),
                        type: "NCoreUIKitIcon",
                        name: "hintTextIcon"
                    },
                    {
                        description: t("dateTimePickerPage.propIsShowHintTextIcon"),
                        name: "isShowHintTextIcon",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propRenderLoadingIcon"),
                        name: "renderLoadingIcon",
                        type: "NCoreUIKitIcon"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimePickerPage.propsEventIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "(value: DateTimePickerResponseType) => void",
                        description: t("dateTimePickerPage.propOnChange"),
                        name: "onChange"
                    },
                    {
                        type: "(value: DateTimePickerResponseType) => void",
                        description: t("dateTimePickerPage.propOnOk"),
                        name: "onOk"
                    },
                    {
                        type: "(value: DateTimePickerResponseType) => void",
                        description: t("dateTimePickerPage.propOnCancel"),
                        name: "onCancel"
                    },
                    {
                        description: t("dateTimePickerPage.propOnFocus"),
                        type: "() => void",
                        name: "onFocus"
                    },
                    {
                        description: t("dateTimePickerPage.propOnBlur"),
                        type: "() => void",
                        name: "onBlur"
                    },
                    {
                        description: t("dateTimePickerPage.propIsWorkWithRealtime"),
                        name: "isWorkWithRealtime",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "(value: { date?, dateRange?, dateRule? }) => boolean",
                        description: t("dateTimePickerPage.propSelectValidation"),
                        name: "selectValidation"
                    },
                    {
                        description: t("dateTimePickerPage.propRemoveSelectValidation"),
                        type: "(value: { date?, dateRange?, dateRule? }) => boolean",
                        name: "removeSelectValidation"
                    },
                    {
                        description: t("dateTimePickerPage.propMinChoice"),
                        name: "minChoice",
                        type: "number"
                    },
                    {
                        description: t("dateTimePickerPage.propMaxChoice"),
                        name: "maxChoice",
                        type: "number"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimePickerPage.propsFormatIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimePickerPage.propTitleFormat"),
                        type: "ValidTitleFormat",
                        name: "titleFormat"
                    },
                    {
                        description: t("dateTimePickerPage.propReturnFormat"),
                        type: "ValidTitleFormat",
                        name: "returnFormat"
                    },
                    {
                        description: t("dateTimePickerPage.propIsWorkWith24HoursFormat"),
                        name: "isWorkWith24HoursFormat",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsWorkWithSeconds"),
                        name: "isWorkWithSeconds",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propDateRangeTitle"),
                        type: "(dateRange?: DateTimePickerDateRange) => string",
                        name: "dateRangeTitle"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimePickerPage.propsCalendarIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimePickerPage.propMinDate"),
                        name: "minDate",
                        type: "Date"
                    },
                    {
                        description: t("dateTimePickerPage.propMaxDate"),
                        name: "maxDate",
                        type: "Date"
                    },
                    {
                        description: t("dateTimePickerPage.propMultipleSelectDayLimit"),
                        name: "multipleSelectDayLimit",
                        type: "number"
                    },
                    {
                        description: t("dateTimePickerPage.propMultipleSelectMinimumRequiredDayCount"),
                        name: "multipleSelectMinimumRequiredDayCount",
                        type: "number"
                    },
                    {
                        description: t("dateTimePickerPage.propMultipleSelectMinimumRequireMS"),
                        name: "multipleSelectMinimumRequireMS",
                        type: "number"
                    },
                    {
                        description: t("dateTimePickerPage.propMultipleSelectMaximumMS"),
                        name: "multipleSelectMaximumMS",
                        type: "number"
                    },
                    {
                        description: t("dateTimePickerPage.propIsStartDateSelectionRequired"),
                        name: "isStartDateSelectionRequired",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsEndDateSelectionRequired"),
                        name: "isEndDateSelectionRequired",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propDateCalendarType"),
                        type: "\"day\" | \"month\" | \"year\"",
                        name: "dateCalendarType"
                    },
                    {
                        description: t("dateTimePickerPage.propIsShowCalendarChangerTool"),
                        name: "isShowCalendarChangerTool",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propMonthOfYearLengthType"),
                        name: "monthOfYearLengthType",
                        type: "\"short\" | \"long\"",
                        defaultValue: "\"short\""
                    },
                    {
                        description: t("dateTimePickerPage.propDayOfWeekLength"),
                        type: "\"short\" | \"very-short\" | \"long\"",
                        defaultValue: "\"short\"",
                        name: "dayOfWeekLength"
                    },
                    {
                        description: t("dateTimePickerPage.propLocaleBasedFirstDayOfWeek"),
                        name: "localeBasedFirstDayOfWeek",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsShowWeekNumber"),
                        name: "isShowWeekNumber",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propMonthCount"),
                        name: "monthCount",
                        defaultValue: "1",
                        type: "1 | 2"
                    },
                    {
                        description: t("dateTimePickerPage.propCaptionLayout"),
                        type: "\"label\" | \"dropdown\"",
                        defaultValue: "\"label\"",
                        name: "captionLayout"
                    },
                    {
                        description: t("dateTimePickerPage.propIsShowTodayIndicator"),
                        name: "isShowTodayIndicator",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsShowDateTimeTools"),
                        name: "isShowDateTimeTools",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dateTimePickerPage.propIsTimeSelectWorksWithCenter"),
                        name: "isTimeSelectWorksWithCenterWhenTimePicker",
                        defaultValue: "true",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimePickerPage.propsSheetIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimePickerPage.propDateTimeSheetProps"),
                        name: "dateTimeSheetProps",
                        type: "IBottomSheetProps"
                    },
                    {
                        description: t("dateTimePickerPage.propCustomDateTimeSheetTheme"),
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        name: "customDateTimeSheetTheme"
                    },
                    {
                        description: t("dateTimePickerPage.propCustomDateTimeSheetLocalize"),
                        name: "customDateTimeSheetLocalize",
                        type: "{ activeLocale }"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("dateTimePickerPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("dateTimePickerPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("dateTimePickerPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("dateTimePickerPage.propCustomKey"),
                        name: "customKey",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                {t("dateTimePickerPage.propsRefIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dateTimePickerPage.refFocus"),
                        type: "() => void",
                        name: "focus"
                    },
                    {
                        description: t("dateTimePickerPage.refBlur"),
                        type: "() => void",
                        name: "blur"
                    },
                    {
                        description: t("dateTimePickerPage.refSetDate"),
                        type: "(date: Date) => void",
                        name: "setDate"
                    },
                    {
                        description: t("dateTimePickerPage.refSetDateRange"),
                        type: "(dateRange: DateTimePickerDateRange) => void",
                        name: "setDateRange"
                    },
                    {
                        description: t("dateTimePickerPage.refSetDateRule"),
                        type: "(dateRule: RRule) => void",
                        name: "setDateRule"
                    },
                    {
                        description: t("dateTimePickerPage.refGetValue"),
                        type: "() => DateTimePickerValueType",
                        name: "getValue"
                    },
                    {
                        description: t("dateTimePickerPage.refClear"),
                        type: "() => void",
                        name: "clear"
                    },
                    {
                        description: t("dateTimePickerPage.refOk"),
                        type: "() => void",
                        name: "ok"
                    },
                    {
                        description: t("dateTimePickerPage.refCancel"),
                        type: "() => void",
                        name: "cancel"
                    },
                    {
                        description: t("dateTimePickerPage.refSetIsLoading"),
                        type: "(state: boolean) => void",
                        name: "setIsLoading"
                    }
                ]}
            />
            <CodeBlock
                code={REF_CODE}
                language="tsx"
            />
            <Paragraph
                isMuted={true}
            >
                {t("dateTimePickerPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default DateTimePickerPage;
